/**
 * ONE date and time implementation, in IST. RC-8.
 *
 * THE DEFECT THIS REPLACES. Every timestamp in the app was rendered in the
 * viewer's local zone with no label, and the server stores UTC. An expense
 * filed at 09:30 IST is stored 04:00Z and reads back as the same day; a visit
 * logged at 00:30 IST is stored 19:00Z THE PREVIOUS DAY, and every surface
 * that buckets by day put it in yesterday. The window is 00:00–05:30 IST, it
 * is silent, and it moves records between days on the report a Director reads.
 *
 * WHAT IS PROMOTED, AND WHAT IS NOT. F390 (Field Map) is the naming source —
 * it is the one surface that ever told the user which zone it meant ("No
 * check-in past 10:00 AM IST"). Its `fmtTime` did NOT pin a zone: it called
 * `toLocaleTimeString([])` and inherited the browser's. So the LABEL is
 * promoted and the IMPLEMENTATION is fixed here — every formatter below names
 * `Asia/Kolkata` explicitly, so a phone set to another zone still shows the
 * company's day.
 *
 * ONE FORMAT PER PURPOSE. The audit found five date formats in play. These are
 * the only ones:
 *
 *   formatISTDate      12 Aug 2026            a date in prose or a table cell
 *   formatISTDayLabel  Wed, 12 Aug            a day in a list or a nav strip
 *   formatISTTime      3:45 PM IST            a clock time
 *   formatISTDateTime  12 Aug 2026, 3:45 PM IST   an audit trail entry
 *   istDayKey          2026-08-12             GROUPING, never display
 *
 * `istDayKey` is the one that fixes the bug rather than the appearance: it is
 * the IST calendar day of an instant, and anything that buckets, groups or
 * compares by day must use it instead of `toISOString().slice(0, 10)`, which
 * is the UTC day.
 *
 * MISSING IS MISSING (Pattern A). Every formatter returns `NO_VALUE` ("—") for
 * null, undefined, empty string or an unparseable value. It never invents
 * "today" and never throws. A caller that needs to say something ABOUT the
 * absence says it in words — see `lib/vocabulary.js` — because "—" is the
 * absence of a value, not a fact about the world.
 */

export const IST_TIME_ZONE = "Asia/Kolkata";
export const IST_LABEL = "IST";

/** What every formatter renders when there is nothing to render. */
export const NO_VALUE = "—";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June",
                     "July", "August", "September", "October", "November", "December"];

/**
 * Parse anything the API hands us into a Date, or null.
 *
 * A DATE-ONLY STRING IS A CALENDAR DAY, NOT AN INSTANT. `new Date("2026-08-12")`
 * is midnight UTC, which is 05:30 IST — so far so harmless — but the same
 * string parsed and then rendered in a zone WEST of UTC lands on the 11th.
 * Date-only values (`expense_date`, `from_date`, a holiday) carry no time and
 * must not acquire one, so they are pinned to IST noon: no zone shift can move
 * noon off its own day.
 */
function toDate(value) {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== "string") return null;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnly) {
    // Noon IST = 06:30Z. Immune to any viewer zone.
    const d = new Date(Date.UTC(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3], 6, 30));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * The IST wall-clock parts of an instant.
 *
 * Built from `Intl.DateTimeFormat` with an explicit `timeZone` rather than
 * from `getHours()` and friends, which read the viewer's zone — the whole
 * defect. `formatToParts` is used instead of parsing a formatted string
 * because part order is locale-dependent and the parts are not.
 */
const PARTS = new Intl.DateTimeFormat("en-GB", {
  timeZone: IST_TIME_ZONE,
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
  weekday: "short",
});

function istParts(value) {
  const d = toDate(value);
  if (!d) return null;
  const out = {};
  for (const p of PARTS.formatToParts(d)) {
    if (p.type !== "literal") out[p.type] = p.value;
  }
  const hour = out.hour === "24" ? 0 : Number(out.hour);   // en-GB midnight
  return {
    year: Number(out.year),
    month: Number(out.month),
    day: Number(out.day),
    hour,
    minute: Number(out.minute),
    weekday: out.weekday,
  };
}

/** `2026-08-12` — the IST calendar day of an instant. For GROUPING only. */
export function istDayKey(value) {
  const p = istParts(value);
  if (!p) return null;
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** The IST hour (0-23) of an instant — for anything that branches on time of
 *  day, which `getHours()` answered in the viewer's zone. */
export function istHour(value) {
  const p = istParts(value);
  return p ? p.hour : null;
}

/** Today's IST calendar day, `2026-08-12`. */
export function todayIST() {
  return istDayKey(new Date());
}

/** `12 Aug 2026` */
export function formatISTDate(value) {
  const p = istParts(value);
  if (!p) return NO_VALUE;
  return `${p.day} ${MONTHS[p.month - 1]} ${p.year}`;
}

/** `Wed, 12 Aug` — a day in a list. Adds the year when it is not this one. */
export function formatISTDayLabel(value) {
  const p = istParts(value);
  if (!p) return NO_VALUE;
  const thisYear = istParts(new Date())?.year;
  const weekday = WEEKDAYS[Math.max(0, WEEKDAYS.indexOf(p.weekday))] || p.weekday;
  const tail = p.year === thisYear ? "" : ` ${p.year}`;
  return `${weekday}, ${p.day} ${MONTHS[p.month - 1]}${tail}`;
}

function clockIST(p) {
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const meridiem = p.hour < 12 ? "AM" : "PM";
  return `${hour12}:${String(p.minute).padStart(2, "0")} ${meridiem}`;
}

/**
 * `3:45 PM IST`. The label is not decoration: a time with no zone is what let
 * a 00:30 IST check-in read as the previous evening without anyone noticing.
 */
export function formatISTTime(value, { label = true } = {}) {
  const p = istParts(value);
  if (!p) return NO_VALUE;
  return label ? `${clockIST(p)} ${IST_LABEL}` : clockIST(p);
}

/** `12 Aug 2026, 3:45 PM IST` */
export function formatISTDateTime(value, { label = true } = {}) {
  const p = istParts(value);
  if (!p) return NO_VALUE;
  const time = label ? `${clockIST(p)} ${IST_LABEL}` : clockIST(p);
  return `${formatISTDate(value)}, ${time}`;
}

/**
 * `2026-08-12` -> a Date at IST noon, for date-picker round trips.
 * Exported so no caller reaches for `new Date(iso)` to get one.
 */
export function istDateOnly(value) {
  return toDate(value);
}

/** Whether two values fall on the same IST calendar day. */
export function sameISTDay(a, b) {
  const ka = istDayKey(a);
  return ka !== null && ka === istDayKey(b);
}

// ---------------------------------------------------------------------------
// Calendar arithmetic, also in IST
//
// These replace four verbatim copies of `isoDate`/`todayIso`/`daysAgoIso` that
// lived in calendarUtils, visitOptions, leaveOptions and expenseOptions. Every
// copy built its day key from `getFullYear/getMonth/getDate` — the viewer's
// local midnight — so "today" on a phone in any other zone was a different day
// from "today" on the server, and the same helper name meant four things.
// ---------------------------------------------------------------------------

/** `2026-08-05` — the IST day `n` days before today. */
export function daysAgoIST(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - Number(n || 0));
  return istDayKey(d);
}

/** `August 2026` — a month heading. Takes (year, month) with month 1-12. */
export function monthLabelIST(year, month) {
  const m = Number(month);
  if (!year || !m || m < 1 || m > 12) return NO_VALUE;
  return `${MONTHS_LONG[m - 1]} ${year}`;
}

/** `August 2026` from any date value — the month-grouping heading. */
export function monthKeyIST(value) {
  const p = istParts(value);
  if (!p) return NO_VALUE;
  return monthLabelIST(p.year, p.month);
}

/**
 * The grid for a month: `{ days: [{ day, iso, weekday }] }`, where `iso` is the
 * IST calendar day and `weekday` is 0=Sun..6=Sat. Built from UTC noon so no
 * cell can slip a day, which the old `new Date(y, m-1, d)` form could.
 */
export function monthDaysIST(year, month) {
  const y = Number(year);
  const m = Number(month);
  if (!y || !m || m < 1 || m > 12) return { days: [] };
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const days = [];
  for (let d = 1; d <= last; d++) {
    const at = new Date(Date.UTC(y, m - 1, d, 6, 30)); // IST noon
    days.push({
      day: d,
      iso: `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      weekday: at.getUTCDay(),
    });
  }
  return { days, first: `${y}-${String(m).padStart(2, "0")}-01`, last: days[days.length - 1].iso };
}

/** The first and last IST day of a month, as `2026-08-01` / `2026-08-31`. */
export function monthRangeIST(year, month) {
  const { first, last } = monthDaysIST(year, month);
  return { first: first || null, last: last || null };
}

/** The IST year and month (1-12) of today, for month-selector state. */
export function currentMonthIST() {
  const p = istParts(new Date());
  return { year: p.year, month: p.month };
}

/**
 * Inclusive day count between two calendar days, `diffDays("2026-08-12",
 * "2026-08-14") === 3`. Counts DAYS, not elapsed milliseconds: the old form
 * divided by 86400000, which is one hour out across a DST boundary — India has
 * none, but the inputs are calendar days and subtracting instants to compare
 * them is the habit that produced the rest of this file.
 */
export function diffDaysIST(fromIso, toIso, halfDay) {
  if (!fromIso || !toIso) return 0;
  if (halfDay && fromIso === toIso) return 0.5;
  const f = istDayKey(fromIso);
  const t = istDayKey(toIso);
  if (!f || !t) return 0;
  const fd = Date.UTC(+f.slice(0, 4), +f.slice(5, 7) - 1, +f.slice(8, 10));
  const td = Date.UTC(+t.slice(0, 4), +t.slice(5, 7) - 1, +t.slice(8, 10));
  if (td < fd) return 0;
  return Math.round((td - fd) / 86400000) + 1;
}
